import React, { useEffect, useRef, useState } from 'react';

import useGlobalState from '../GlobalStateContext';
import { OptionalRolesOrPermissions } from '../helpers/Permissions';
import handleValidationErrors, { ValidationErrors } from '../helpers/ResponseErrorHelper';
import useBlockingSubmit from '../hooks/useBlockingSubmit';

import FormInvalid from './formElements/FormInvalid';

type Props = {
    parameterName: string;
    value: string | number;
    action: (value: string) => Promise<void>;
    displayName: string;
    className?: string;
} & OptionalRolesOrPermissions;

export default function ModifyParameterModal({
    parameterName,
    value,
    action,
    displayName,
    permissions,
    roles,
    className = '',
}: Props) {
    const inputElement = useRef<HTMLInputElement>();
    const [modalShown, setModalShown] = useState<boolean>(false);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const hasError = (errors[parameterName] ?? false) !== false;
    const [Submit, submitBlocked] = useBlockingSubmit();
    const { loggedAdmin } = useGlobalState();
    const [inputValue, setInputValue] = useState(value);

    useEffect(() => setInputValue(value), [value]);
    useEffect(() => {
        if (inputElement.current && modalShown) {
            inputElement.current.select();
        }
    }, [modalShown]);

    async function handleUpdateNewValue(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setErrors({});
        try {
            await action(inputValue.toString());
            setModalShown(false);
        } catch (error) {
            handleValidationErrors(error, setErrors);
        } finally {
            submitBlocked(false);
        }
    }

    return loggedAdmin.hasAccess(roles, permissions) ? (
        <>
            <button
                type="button"
                className={`btn btn-primary btn-sm btn-sm-margin ${className}`}
                onClick={() => setModalShown(true)}
                data-target={`#${parameterName}Modal`}
            >
                Change {displayName}
            </button>
            <div
                id={`${parameterName}Modal`}
                className={`modal parameter-modal ${modalShown ? 'shown' : 'hidden'}`}
                tabIndex={-1}
                role="dialog"
                aria-labelledby="myModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <form onSubmit={handleUpdateNewValue}>
                            <div className="modal-header">
                                <h4 className="modal-title" id="myModalLabel">
                                    Change {displayName}
                                </h4>
                                <button
                                    type="button"
                                    className="close"
                                    onClick={() => setModalShown(false)}
                                    aria-hidden="true"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor={parameterName}>{displayName}</label>
                                    <input
                                        id={parameterName}
                                        className={`form-control ${hasError ? 'is-invalid' : ''}`}
                                        name={parameterName}
                                        value={inputValue}
                                        ref={inputElement}
                                        onChange={(e) => setInputValue(e.currentTarget.value)}
                                    />
                                    {hasError && <FormInvalid message={errors[parameterName][0]} />}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-light" onClick={() => setModalShown(false)}>
                                    Close
                                </button>
                                <Submit label="Save changes" />
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    ) : null;
}
